import type { FC } from 'react'
import { useMemo } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'

import type { Command } from '@/index'
import { formatTimestamp } from '@/common/utils'
import { CopyButton } from '@/common/components'
import {
  isInterrupt,
  isPrompt,
  isQuestionnaireAnswer,
  isResume,
  isUserOptionSelection,
} from '@/features/events/conversation/state/types'

import { MarkdownRenderer } from '../markdown'
import { FIXED_TIMESTAMP_SIZE } from '../shared'
import { DEFAULT_BUBBLE_STYLES } from './defaults'
import { createBubblePaperSx } from './styles'

interface ConversationCommandBubbleProps {
  readonly command: Command
}

export const ConversationCommandBubble: FC<ConversationCommandBubbleProps> = ({ command }) => {
  const theme = useTheme()
  const content = useMemo(() => resolveConversationCommandContent(command), [command])
  const copyText = command.userPrompt || content

  if (isInterrupt(command) || isResume(command) || isQuestionnaireAnswer(command)) {
    return null
  }

  if (isUserOptionSelection(command)) {
    return null
  }

  return (
    <Box sx={conversationCommandBubbleContainerSx}>
      <Box
        sx={createBubblePaperSx(
          theme,
          theme.palette.info.main,
          'right',
          DEFAULT_BUBBLE_STYLES.bubbles.user,
        )}
      >
        <Box sx={conversationCommandContentSx}>
          <MarkdownRenderer content={content} />
        </Box>
        <Box className="bubble-footer" sx={footerSx}>
          <Box className="copy-button" sx={copyButtonContainerSx}>
            <CopyButton text={copyText} variant="small" />
          </Box>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={conversationCommandTimestampSx}>
        {formatTimestamp(command.timestamp)}
      </Typography>
    </Box>
  )
}

function resolveConversationCommandContent(command: Command): string {
  if (isPrompt(command)) {
    return command.userPrompt || ''
  }

  return ''
}

const conversationCommandBubbleContainerSx: SxProps<Theme> = {
  width: '100%',
  mb: 2,
}

const conversationCommandContentSx: SxProps<Theme> = {
  mb: 0,
}

const footerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  mt: 0,
  maxHeight: 0,
  overflow: 'hidden',
  opacity: 0,
  transition: 'max-height 0.2s ease-in-out, opacity 0.2s ease-in-out, margin-top 0.2s ease-in-out',
}

const conversationCommandTimestampSx: SxProps<Theme> = {
  display: 'block',
  mt: 0.5,
  textAlign: 'right',
  fontSize: FIXED_TIMESTAMP_SIZE,
}

const copyButtonContainerSx: SxProps<Theme> = {
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
}
